import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ThemeToggle from '../components/ThemeToggle';
import api from '../services/api';
import { useAuth } from '../contexts/KStackAuthContext';
import { diag } from '../services/diagnostics';
import './Settings.css';

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [loading, setLoading] = useState(false);
  const handleSignOut = async () => { await signOut(); navigate('/login', { replace: true }); };
  // BOLUS_NOTES-29: diagnostics flag + recent events
  const [diagOn, setDiagOn] = useState<boolean>(diag.enabled);
  const [diagRows, setDiagRows] = useState<any[]>([]);
  const toggleDiag = async () => {
    try { setDiagOn(await diag.setFlag(!diagOn, 24)); } catch (e) { console.error(e); }
  };
  const loadDiag = async () => {
    try { const r = await diag.list({ limit: 100 }); setDiagRows(r?.events || []); setDiagOn(!!r?.flag?.enabled); } catch (e) { console.error(e); }
  };
  const [message, setMessage] = useState<{ text: string, type: 'success' | 'error' } | null>(null);

  const handleBackup = async () => {
    setLoading(true);
    setMessage(null);
    try {
      await api.post('/v1/app/bolus-notes/db/backup');
      setMessage({ text: 'Database backup completed successfully.', type: 'success' });
    } catch (err: any) {
      console.error(err);
      setMessage({ text: err.response?.data?.error?.message || 'Backup failed.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setLoading(true);
    setMessage(null);
    try {
      // Trigger the Dexcom sync job or generic poll job
      await api.post('/v1/jobs/bolus-notes/trigger/dexcom-poll');
      setMessage({ text: 'Sync triggered successfully! Background job running.', type: 'success' });
    } catch (err: any) {
      console.error(err);
      setMessage({ text: err.response?.data?.error?.message || 'Failed to trigger sync.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ios-dashboard-container settings-container">
      <header className="ios-header">
        <div className="header-left">
          <button className="icon-btn" onClick={() => navigate('/dashboard')}>←</button>
          <h1>Settings</h1>
        </div>
        <div className="header-actions">
          <ThemeToggle placement="header" />
        </div>
      </header>

      <main className="ios-content">
        <div className="settings-section">
          <h2>Account</h2>
          <div className="settings-card">
            <div className="settings-row">
              <div className="settings-info">
                <h3>Signed in as {user?.email || user?.username || '—'}</h3>
                <p>Sign out ends this session on this browser and revokes its refresh token on the server.</p>
              </div>
              <button className="btn-setup" onClick={handleSignOut} disabled={loading}>Sign out</button>
            </div>
          </div>
        </div>

        <div className="settings-section">
          <h2>Diagnostics</h2>
          <div className="settings-card">
            <div className="settings-row">
              <div className="settings-info">
                <h3>Client diagnostics {diagOn ? '— ON (all your devices, 24 h)' : '— off'}</h3>
                <p>When on, this browser, the iPhone, the Watch and the widget log what they do (stream, polling, auth, widget reloads) to the server so we can see exactly what happened instead of guessing. Never includes passwords or tokens. Auto-expires.</p>
              </div>
              <button className="btn-setup" onClick={toggleDiag}>{diagOn ? 'Turn off' : 'Turn on'}</button>
            </div>
            <div className="settings-row">
              <div className="settings-info">
                <h3>Recent events</h3>
                <p>Newest first, all devices.</p>
              </div>
              <button className="btn-setup" onClick={loadDiag}>Load</button>
            </div>
            {diagRows.length > 0 && (
              <div style={{ maxHeight: 320, overflow: 'auto', fontFamily: 'monospace', fontSize: 12 }}>
                {diagRows.map((r) => (
                  <div key={r.id} style={{ padding: '2px 0', borderBottom: '1px solid rgba(128,128,128,0.2)' }}>
                    {new Date(r.ts).toLocaleTimeString()} · {r.platform} · {r.category}/{r.event} {r.detail ? JSON.stringify(r.detail) : ''}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="settings-section">
          <h2>Database Management</h2>
          <div className="settings-card">
            <div className="settings-row">
              <div className="settings-info">
                <h3>Manual Backup</h3>
                <p>Create an immediate backup of the BolusNotes database.</p>
              </div>
              <button className="btn-setup" onClick={handleBackup} disabled={loading}>
                {loading ? 'Running...' : 'Backup Now'}
              </button>
            </div>
            
            <div className="settings-row">
              <div className="settings-info">
                <h3>Restore Database</h3>
                <p>Restore the database from a previous backup snapshot.</p>
              </div>
              <button className="btn-setup" disabled={true} style={{ opacity: 0.5 }}>
                Coming Soon
              </button>
            </div>
          </div>
        </div>

        <div className="settings-section">
          <h2>Polling Configuration</h2>
          <div className="settings-card">
             <div className="settings-row">
              <div className="settings-info">
                <h3>Force Background Sync</h3>
                <p>Manually trigger the background daemon to fetch the latest entries.</p>
              </div>
              <button className="btn-setup highlight" onClick={handleSync} disabled={loading}>
                {loading ? 'Triggering...' : 'Sync Now'}
              </button>
            </div>
            <div className="settings-row">
              <div className="settings-info">
                <h3>Follower Accounts</h3>
                <p>Manage people who can view or contribute to your diabetes data.</p>
              </div>
              <button className="btn-setup" onClick={() => navigate('/followers')}>
                Manage
              </button>
            </div>
          </div>
        </div>

        <div className="settings-section">
          <h2>Account Management</h2>
          <div className="settings-card">
            <div className="settings-row">
              <div className="settings-info">
                <h3>Sign Out</h3>
                <p>Securely log out of the BolusNotes application and clear session credentials.</p>
              </div>
              <button 
                className="btn-setup" 
                onClick={() => {
                  localStorage.removeItem('bolusnotes_auth_tokens');
                  localStorage.removeItem('kstack_user_profile');
                  navigate('/login');
                }} 
                style={{ color: 'var(--accent-red)', borderColor: 'var(--accent-red)' }}
              >
                Log Out
              </button>
            </div>
          </div>
        </div>

        {message && (
          <div className={`status-message ${message.type}`}>
            {message.text}
          </div>
        )}
      </main>
    </div>
  );
};

export default Settings;
